import { Button, Col, Form, Input, Row, Select, Radio, Space } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getFilterList } from 'api/Activity'

const toOptionId = (item: any) => item?._id || item?.id

const AddActivityForm = ({ onSubmit, onCancel, initialValues, isEdit = false, loading = false }: any) => {
  const [form] = Form.useForm()
  const { data: filterData } = useQuery(['getFilterList'], () => getFilterList())

  const organizationOptions = filterData?.organizations?.map((item: any) => ({
    label: item.name,
    value: toOptionId(item),
  }))

  const categoryOptions = filterData?.activityCagegories?.map((item: any) => ({
    label: item.name,
    value: toOptionId(item),
  }))

  const highlightedElementOptions = filterData?.highlightedElements?.map((item: any) => ({
    label: item.name,
    value: toOptionId(item),
  }))

  const goalOptions = filterData?.goals?.map((item: any) => ({
    label: item.name,
    value: toOptionId(item),
  }))

  const energyLevelOptions = filterData?.energyLevels?.map((item: any) => ({
    label: item.name,
    value: toOptionId(item),
  }))

  const environmentOptions = (filterData?.environments || ['indoors', 'outdoors']).map((value: string) => ({
    label: value.charAt(0).toUpperCase() + value.slice(1),
    value,
  }))

  const timeOptions = (filterData?.times || ['30 mins', '45 mins', 'other']).map((value: string) => ({
    label: value,
    value,
  }))

  useEffect(() => {
    if (initialValues) {
      const resolveId = (value: any) =>
        value && typeof value === 'object' ? value._id || value.id : value

      form.setFieldsValue({
        organizationId:
          initialValues.organizationId ||
          initialValues.organization?._id ||
          initialValues.organization?.id,
        name: initialValues.name,
        categoryIds: (initialValues.categoryIds || []).map((id: any) => resolveId(id)),
        highlightedElementId: resolveId(initialValues.highlightedElementId),
        goalId: resolveId(initialValues.goalId),
        energyLevelId: resolveId(initialValues.energyLevelId),
        environment: initialValues.environment,
        time: initialValues.time,
        plan: initialValues.plan,
        galleryIds: initialValues.galleryIds || [],
      })
    } else {
      form.resetFields()
    }
  }, [initialValues, form])

  const handleSubmit = () => {
    form.validateFields().then(values => {
      const { organizationId, ...rest } = values
      onSubmit({
        organizationId,
        payload: {
          name: rest.name,
          categoryIds: rest.categoryIds || [],
          highlightedElementId: rest.highlightedElementId,
          goalId: rest.goalId,
          energyLevelId: rest.energyLevelId,
          environment: rest.environment,
          time: rest.time,
          plan: rest.plan || '',
          galleryIds: rest.galleryIds || [],
        },
      })
    })
  }

  return (
    <div>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          {!isEdit && (
            <Col span={24}>
              <label className="input-label">Organization</label>
              <Form.Item
                name="organizationId"
                rules={[{ required: true, message: 'Please select an organization' }]}
              >
                <Select
                  placeholder="Select Organization"
                  options={organizationOptions}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <label className="input-label">Activity name</label>
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please enter activity name' }]}
            >
              <Input placeholder="Activity name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Category</label>
            <Form.Item
              name="categoryIds"
              rules={[{ required: true, message: 'Please select at least one category' }]}
            >
              <Select mode="multiple" placeholder="Select categories" options={categoryOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Highlighted Element</label>
            <Form.Item
              name="highlightedElementId"
              rules={[{ required: true, message: 'Please select highlighted element' }]}
            >
              <Select placeholder="Select highlighted element" options={highlightedElementOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Goal</label>
            <Form.Item name="goalId" rules={[{ required: true, message: 'Please select a goal' }]}>
              <Select placeholder="Select goal" options={goalOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Energy Level</label>
            <Form.Item
              name="energyLevelId"
              rules={[{ required: true, message: 'Please select energy level' }]}
            >
              <Select placeholder="Select energy level" options={energyLevelOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Environment</label>
            <Form.Item
              name="environment"
              rules={[{ required: true, message: 'Please select environment' }]}
            >
              <Radio.Group options={environmentOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Time</label>
            <Form.Item name="time" rules={[{ required: true, message: 'Please select time' }]}>
              <Select placeholder="Select time" options={timeOptions} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <label className="input-label">Plan</label>
            <Form.Item name="plan" rules={[{ required: true, message: 'Please enter activity plan' }]}>
              <TextArea rows={4} placeholder="Activity plan / description" />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Space size={10}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button className="stepper-button" type="primary" onClick={handleSubmit} loading={loading}>
              Save
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  )
}

export default AddActivityForm
